import { PropsWithChildren, useRef, createContext, useContext, useState, CSSProperties, useEffect } from 'react'
import Styles from './Drawer.module.scss'
import cx from 'classnames'
import { CSSTransition } from 'react-transition-group'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Close } from 'blaze-ui/icons'

interface DrawerProps {
  open: boolean
  position?: 'left' | 'right'
  onClose: () => void
  closeOnClickOutside?: boolean
  styles?: {
    width?: number
    top?: number
    offset?: number
    borderRadius?: number
    backgroundColor?: string
  }
  animated?: boolean
  className?: string
}

interface DrawerContextProps {
  onClose?: () => void
  hasHeader?: boolean
  setHasHeader?: (hasHeader: boolean) => void
}

const DrawerContext = createContext<DrawerContextProps>({})

const Header = ({ children }: PropsWithChildren<{}>) => {
  const { setHasHeader, hasHeader } = useContext(DrawerContext)

  useEffect(() => {
    if (!hasHeader) {
      setHasHeader?.(true)
    }
  }, [hasHeader, setHasHeader])

  return (
    <div className={Styles.header}>
      <div className={Styles.headerInner}>{children}</div>
    </div>
  )
}

const CloseButton = () => {
  const { onClose } = useContext(DrawerContext)

  return <IconButton variant="tertiary" size="sm" icon={Close} onPress={onClose} aria-label="Close" />
}

const Body = ({ children }: PropsWithChildren<{}>) => {
  const { hasHeader } = useContext(DrawerContext)

  return <div className={cx(Styles.body, { [Styles.hasHeader]: hasHeader })}>{children}</div>
}

export const Drawer = ({
  open,
  position = 'right',
  onClose,
  closeOnClickOutside,
  styles = {
    width: 300,
    top: 0,
    offset: 0,
    borderRadius: 0,
    backgroundColor: 'var(--light-80)',
  },
  animated = true,
  children,
  className,
}: PropsWithChildren<DrawerProps>) => {
  const drawerRef = useRef<HTMLDivElement>(null)
  const [hasHeader, setHasHeader] = useState(false)

  return (
    <DrawerContext.Provider value={{ onClose, hasHeader, setHasHeader }}>
      {closeOnClickOutside && (
        <CSSTransition
          in={open}
          timeout={300}
          classNames={{
            enter: Styles.overlayEnter,
            enterActive: Styles.overlayEnterActive,
            exit: Styles.overlayExit,
            exitActive: Styles.overlayExitActive,
          }}
          unmountOnExit
        >
          <div className={Styles.overlay} onClick={onClose} data-testid="overlay" />
        </CSSTransition>
      )}
      <div
        data-testid="drawer"
        ref={drawerRef}
        className={cx(
          Styles.drawerContainer,
          className,
          { [Styles.open]: open },
          { [Styles[position]]: position }, // assuming `position` is a variable
          { [Styles.animated]: animated }, // if `animated` is a boolean condition
        )}
        style={
          {
            '--offset': position === 'left' ? `${styles.offset}px` : `-${styles.offset}px`,
            width: styles.width,
            top: styles.top,
            backgroundColor: styles.backgroundColor,
            height: `calc(100% - ${styles.top}px)`,
            borderRadius: `${styles.borderRadius}px ${styles.borderRadius}px 0 0`,
          } as CSSProperties
        }
      >
        {children}
      </div>
    </DrawerContext.Provider>
  )
}

Drawer.Header = Header
Drawer.CloseButton = CloseButton
Drawer.Body = Body
